import { Formik } from 'formik';
import React, { useReducer } from 'react';
import { constants } from '@clubmed/utils';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer';
import { LineTitle } from 'Components/widgets/line-title/LineTitle';
import { useAppContext } from 'Context/ReactAppContext';
import { t } from 'Src/i18n';
import { MEETING_EVENT } from 'Tracking/constants';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import graphQLClient from 'Utils/graphql-client/graphqlClient';
import { WIDGET_EVENT_FORM_SUBMIT } from 'Utils/request/requestDictionary';
import {
  getEventQuery,
  getEventSchema,
  getEventAffinitySchema,
  getInitialFormValues,
  FORM_ACTIONS,
  FORM_STATUS,
} from 'Components/widgets/event-form/eventForm.utils';
import { EventFormForm } from 'Components/widgets/event-form/EventFormForm';
import { EventFormFields } from 'Components/widgets/event-form/EventFormFields';
import { EventFormAffinityFields } from 'Components/widgets/event-form/EventFormAffinityFields';
type EventFormProps = {
  type: 'classic' | 'affinity';
  displayAddressFields: boolean;
  typeOptions: Array<{
    value: string;
    name: string;
  }>;
  formError: EventFormResponseTranslations;
  formSuccess: EventFormResponseTranslations;
  regions: Array<{
    value: string;
    name: string;
  }>;
  resorts: Array<EventFormResort>;
  userSalutationOptions: Array<{
    value: string;
    name: string;
  }>;
};
const { OPENING_STATUS } = constants;
export const EventForm = ({
  type,
  displayAddressFields = true,
  typeOptions,
  formError,
  formSuccess,
  regions,
  resorts,
  userSalutationOptions,
}: EventFormProps) => {
  const appContext = useAppContext();
  const track = useTracking({
    categoryGA: MEETING_EVENT.REQUEST,
  });
  const [{ isSending, formStatus, confirmation }, dispatch] = useReducer(
    (state, action) => {
      switch (action.type) {
        case FORM_ACTIONS.SUBMIT_EVENT:
          return { ...state, isSending: true };

        case FORM_ACTIONS.SUBMIT_EVENT_ERROR:
          return {
            isSending: false,
            formStatus: FORM_STATUS.ERROR,
            confirmation: formError,
          };

        case FORM_ACTIONS.SUBMIT_EVENT_SUCCESS:
          return {
            isSending: false,
            formStatus: FORM_STATUS.SUCCESS,
            confirmation: formSuccess,
          };

        default:
          return state;
      }
    },
    {
      isSending: false,
      formStatus: FORM_STATUS.NEW,
      confirmation: {},
    },
  );

  const onFormSubmit = async (values) => {
    dispatch({
      type: FORM_ACTIONS.SUBMIT_EVENT,
    });
    const query = getEventQuery(values, type);
    const labelGA = values.resort || null;

    try {
      const { event }: EventFormEvent = await graphQLClient.runNoCacheQuery(query, appContext, {
        requestName: WIDGET_EVENT_FORM_SUBMIT,
      });

      if (event.errorCode) {
        dispatch({
          type: FORM_ACTIONS.SUBMIT_EVENT_ERROR,
        });
        track({
          actionGA: MEETING_EVENT.SEND_REQUEST_ERROR,
          labelGA,
        });
      } else {
        dispatch({
          type: FORM_ACTIONS.SUBMIT_EVENT_SUCCESS,
        });
        track({
          actionGA: MEETING_EVENT.SEND_REQUEST_SUCCESS,
          labelGA,
        });
      }
    } catch (error) {
      dispatch({
        type: FORM_ACTIONS.SUBMIT_EVENT_ERROR,
      });
      track({
        actionGA: MEETING_EVENT.SEND_REQUEST_ERROR,
        labelGA,
      });
    }
  };

  const openedResorts = resorts.filter(
    ({ openingStatus }) => openingStatus === OPENING_STATUS.OPEN,
  );
  const schemaArgs = {
    displayAddressFields,
    resortIds: openedResorts.map((resort) => resort.id),
  };
  return (
    <SiteContainer className="py-40" id="event-form">
      <LineTitle>{t('eventForm.formTitle')}</LineTitle>
      <Formik
        initialValues={getInitialFormValues()}
        validationSchema={
          type === 'classic' ? getEventSchema(schemaArgs) : getEventAffinitySchema(schemaArgs)
        }
        onSubmit={onFormSubmit}
      >
        <EventFormForm isSending={isSending} formStatus={formStatus} confirmation={confirmation}>
          {type === 'classic' ? (
            <EventFormFields
              displayAddressFields={displayAddressFields}
              typeOptions={typeOptions}
              regions={regions}
              resorts={resorts}
              userSalutationOptions={userSalutationOptions}
            />
          ) : (
            <EventFormAffinityFields
              displayAddressFields={displayAddressFields}
              typeOptions={typeOptions}
              regions={regions}
              resorts={resorts}
            />
          )}
        </EventFormForm>
      </Formik>
    </SiteContainer>
  );
};
